import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { InviteTarget } from '../types';
import { inviteAPI } from '@/api/inviteAPI';
import type { OrganizationNode, OrgEmployeeNode, OrgSearchResultItem } from '@/types/employee';
import { toAbsoluteProfileUrl } from '@/utils/profileUtils';

// Stable default values to prevent new references on every render
const EMPTY_SET_NUMBER = new Set<number>();
const EMPTY_ARRAY_SEARCH: OrgSearchResultItem[] = [];

// Local aliases for backwards compat within this file
type EmployeeNode = OrgEmployeeNode;
type SearchResultItem = OrgSearchResultItem;

interface OrganizationTabProps {
  query: { text: string; version: number };
  selectedUsers: InviteTarget[];
  onToggleUser: (user: InviteTarget) => void;
  onRemoveUserFromOrg: (orgId: string, memberToExclude: InviteTarget, allOrgMembers: InviteTarget[]) => void;
  cno: string | null;
  companyNo: string | null;
  myUserNo?: number;
  connectedPortalIds: Set<string>;
}

// 노드 또는 그 자손이 주어진 Set에 포함되어 있는지 확인
const hasDescendantInSet = (node: OrganizationNode, set: Set<number>): boolean => {
  if (set.has(node.organization_no)) return true;
  if (node.children) {
    return node.children.some(child => hasDescendantInSet(child, set));
  }
  return false;
};

const TreeNode: React.FC<{
  node: OrganizationNode;
  employeesMap: Record<string, EmployeeNode[]>;
  selectedIds: Set<string>;
  onToggleUser: (user: InviteTarget) => void;
  onRemoveUserFromOrg: (orgId: string, memberToExclude: InviteTarget, allOrgMembers: InviteTarget[]) => void;
  cno: string | null;
  companyNo: string | null;
  depth?: number;
  parentChecked?: boolean;
  expandedNodes?: Set<number>;
  highlightedUserNos?: Set<number>;
  highlightedOrgNos?: Set<number>;
  allSearchResults?: SearchResultItem[];
  connectedPortalIds: Set<string>;
}> = ({
  node,
  employeesMap,
  selectedIds,
  onToggleUser,
  onRemoveUserFromOrg,
  cno,
  companyNo,
  depth = 0,
  parentChecked = false,
  expandedNodes = EMPTY_SET_NUMBER,
  highlightedUserNos = EMPTY_SET_NUMBER,
  highlightedOrgNos = EMPTY_SET_NUMBER,
  allSearchResults = EMPTY_ARRAY_SEARCH,
  connectedPortalIds
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const shouldForceOpen = hasDescendantInSet(node, expandedNodes);

  useEffect(() => {
    if (shouldForceOpen) {
      setIsOpen(true);
    }
  }, [shouldForceOpen]);

  // 실시간 직원 조회 (확장 시에만)
  const { data: dynamicEmployees = [] } = useQuery({
    queryKey: ['orgEmployees', node.organization_no, cno, companyNo],
    queryFn: async (): Promise<EmployeeNode[]> => {
      if (!cno || !companyNo) return [];
      const res = await inviteAPI.getOrganizationEmployees(cno, companyNo, String(node.organization_no));
      return res?.resultData || [];
    },
    enabled: isOpen && !!cno && !!companyNo,
    staleTime: 1000 * 60 * 5,
  });

  // 초기 로드 데이터 + 실시간 데이터 + 검색 결과 데이터 병합
  const mergedEmployees = useMemo(() => {
    const base = employeesMap[node.organization_no] || [];
    
    // 이 조직에 속한 검색 결과 매핑
    const searchMatches: EmployeeNode[] = allSearchResults
      .filter(r => Number(r.organization_no) === Number(node.organization_no) && r.user_no)
      .map(r => ({
        employee_no: r.employee_no,
        user_no: r.user_no,
        user_name: r.user_name,
        rank_name: r.rank_name,
        company_no: r.company_no,
        portal_id: r.portal_id,
        profile_url: r.profile_url,
        user_default_email: r.user_default_email
      }));

    // 중복 제거를 위한 맵 처리 (user_no 기준)
    const empMap = new Map<number, EmployeeNode>();
    [...base, ...dynamicEmployees, ...searchMatches].forEach(e => {
      if (e.user_no) empMap.set(e.user_no, e);
    });
    
    return Array.from(empMap.values());
  }, [node.organization_no, employeesMap, dynamicEmployees, allSearchResults]);

  // 자식 조직 노드들 반환
  const childrenNodes = node.children || [];

  const orgId = `ORG_${node.organization_no}`;
  const isSelected = selectedIds.has(orgId);
  const effectiveChecked = isSelected || parentChecked;

  // 조직 자체를 토글했을 때 하위 멤버 일괄 조작 여부 - 현재 하위 인원 모두 토글
  const handleToggleOrg = () => {
    if (parentChecked) return; // 상위가 체크된 경우 하위는 수정 불가하도록 처리 (기획적 단순화)

    if (isSelected) {
      // 체크 해제
      onToggleUser({ id: orgId, type: 'ORGANIZATION', name: node.organization_name });
    } else {
      // 이 노드 아래 모든 조직의 id 수집 (재귀)
      const gatherAllEmployees = (n: OrganizationNode): {userNo: number, companyNo: number, portalId: string, userName: string, profileUrl: string}[] => {
        let list = (employeesMap[n.organization_no] || []).map(e => ({ userNo: e.user_no, companyNo: e.company_no, portalId: e.portal_id || '', userName: e.user_name || '', profileUrl: toAbsoluteProfileUrl(e.profile_url) || '' }));
        if (n.children) {
           n.children.forEach(child => {
             list = list.concat(gatherAllEmployees(child));
           });
        }
        return list;
      };

      onToggleUser({
        id: orgId,
        type: 'ORGANIZATION',
        name: node.organization_name,
        members: gatherAllEmployees(node)
      });
    }
  };

  return (
    <div className="rtcOrganization__item" style={{ paddingLeft: depth > 0 ? 15 : 0 }}>
      {/* 부서 노드 */}
      <div className={`rtcOrganization__itemCon ${highlightedOrgNos.has(node.organization_no) ? 'highlight' : ''}`}>
        <div className="rtcOrganization__itemLeft">
          <button type="button" className="rtcOrganization__itemLeftBtn" onClick={() => setIsOpen(!isOpen)}>
            <span className={`rtcOrganization__icon ${depth === 0 ? 'icon_company' : (!isOpen ? 'icon_fold' : 'icon_foldminus')}`}></span>
            <span className="name ellipsis">{node.organization_name}</span>
            <em className="count">{node.employee_count}</em>
          </button>
        </div>
        <div className="rtcOrganization__itemRight">
          <div className="common_radio_input type_agree5 rtcNewType">
            <label>
              {/* 부서 전체 체크 로직 */}
              <input 
                className="radio_input" 
                type="checkbox" 
                checked={effectiveChecked} 
                onChange={handleToggleOrg} 
                disabled={parentChecked} 
              />
              <span className="icon"></span>
            </label>
          </div>
        </div>
      </div>

      {/* 부서 내부 렌더링 */}
      {isOpen && (
        <div className="rtcOrganization__itemChildren">
          {childrenNodes.map(child => (
             <TreeNode
               key={`org_${child.organization_no}`}
               node={child}
               employeesMap={employeesMap}
               selectedIds={selectedIds}
               onToggleUser={onToggleUser}
               onRemoveUserFromOrg={onRemoveUserFromOrg}
               cno={cno}
               companyNo={companyNo}
               depth={depth + 1}
               parentChecked={effectiveChecked}
               expandedNodes={expandedNodes}
               highlightedUserNos={highlightedUserNos}
               highlightedOrgNos={highlightedOrgNos}
               allSearchResults={allSearchResults}
               connectedPortalIds={connectedPortalIds}
             />
          ))}

          {mergedEmployees.map(emp => {
            const targetId = `USER_${emp.user_no}_${emp.company_no}`;
            const isMemberSelected = selectedIds.has(targetId);
            const isAlreadyJoined = Boolean(emp.portal_id && connectedPortalIds.has(emp.portal_id));
            const isHighlighted = highlightedUserNos.has(emp.user_no) && !isAlreadyJoined;

            const memberTarget: InviteTarget = {
              id: targetId,
              type: 'USER',
              name: emp.user_name || '이름없음',
              userNo: emp.user_no,
              companyNo: emp.company_no,
              portalId: emp.portal_id,
              profileUrl: toAbsoluteProfileUrl(emp.profile_url),
              department: node.organization_name
            };

            return (
              <div
                key={targetId}
                className={`rtcOrganization__item ${isHighlighted ? 'highlight' : ''}`}
                style={{ paddingLeft: 15 }}
              >
                <div className="rtcOrganization__itemCon">
                  <div className="rtcOrganization__itemLeft">
                    <button type="button" className="rtcOrganization__itemLeftBtn">
                      <span className="rtcOrganization__icon icon_user"></span>
                      <span className="name ellipsis">{emp.user_name} {emp.rank_name}</span>
                    </button>
                  </div>
                  {!isAlreadyJoined && (
                    <div className="rtcOrganization__itemRight">
                      <div className="common_radio_input type_agree5 rtcNewType">
                        <label>
                          <input
                            className="radio_input"
                            type="checkbox"
                            checked={isMemberSelected || effectiveChecked}
                            disabled={parentChecked}
                            onChange={() => {
                              if (parentChecked) return;
                              if (isSelected && !isMemberSelected) {
                                // 직접 부모 조직이 선택된 상태에서 개별 멤버 해제 → 조직 분해
                                // 하위 조직 멤버까지 재귀적으로 수집
                                const gatherAllDescendants = (n: OrganizationNode): InviteTarget[] => {
                                  const emps: InviteTarget[] = (employeesMap[n.organization_no] || []).map(e => ({
                                    id: `USER_${e.user_no}_${e.company_no}`,
                                    type: 'USER' as const,
                                    name: e.user_name || '이름없음',
                                    userNo: e.user_no,
                                    companyNo: e.company_no,
                                    portalId: e.portal_id,
                                    profileUrl: toAbsoluteProfileUrl(e.profile_url),
                                    department: n.organization_name
                                  }));
                                  let result = [...emps];
                                  if (n.children) {
                                    n.children.forEach(child => {
                                      result = result.concat(gatherAllDescendants(child));
                                    });
                                  }
                                  return result;
                                };
                                const allOrgMembers = gatherAllDescendants(node);
                                onRemoveUserFromOrg(orgId, memberTarget, allOrgMembers);
                              } else {
                                onToggleUser(memberTarget);
                              }
                            }}
                          />
                          <span className="icon"></span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// 경로 탐색 함수: 특정 organization_no들을 포함하는 모든 부모 노드의 org_no를 수집
const findAllPathNos = (
  nodes: OrganizationNode[],
  targets: Set<number>,
  result: Set<number>
): boolean => {
  let foundAny = false;
  for (const node of nodes) {
    const childFound = findAllPathNos(node.children || [], targets, result);
    if (targets.has(node.organization_no) || childFound) {
      result.add(node.organization_no);
      foundAny = true;
    }
  }
  return foundAny;
};

const OrganizationTab: React.FC<OrganizationTabProps> = ({ query, selectedUsers, onToggleUser, onRemoveUserFromOrg, cno, companyNo, myUserNo, connectedPortalIds }) => {
  const { data: baseData, isLoading } = useQuery<{ treeData: OrganizationNode[], employeesMap: Record<string, EmployeeNode[]> }>({
    queryKey: ['organizationTree', cno, companyNo],
    queryFn: async () => {
      if (!cno || !companyNo) return { treeData: [], employeesMap: {} };
      const [treeRes, empRes] = await Promise.all([
        inviteAPI.getOrganizationTree(cno, companyNo),
        inviteAPI.getOrganizationEmployees(cno, companyNo, '', true)
      ]);
      return {
        treeData: treeRes?.resultData || [],
        employeesMap: empRes?.resultData || {}
      };
    },
    enabled: !!cno && !!companyNo,
    staleTime: 1000 * 60 * 5, // 5분 캐시
  });

  const treeData = useMemo(() => baseData?.treeData || [], [baseData?.treeData]);
  const employeesMap = useMemo(() => baseData?.employeesMap || {}, [baseData?.employeesMap]);

  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [highlightedUserNos, setHighlightedUserNos] = useState<Set<number>>(new Set());
  const [highlightedOrgNos, setHighlightedOrgNos] = useState<Set<number>>(new Set());
  const [scrollTargetIndex, setScrollTargetIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);

  const selectedIds = useMemo(() => {
    return new Set(selectedUsers.map(u => u.id));
  }, [selectedUsers]);

  // 본인이 속한 조직을 초기에 자동 펼침
  const myOrgExpandedNodes = useMemo(() => {
    if (!myUserNo || Object.keys(employeesMap).length === 0 || treeData.length === 0) return new Set<number>();
    // employeesMap에서 myUserNo가 속한 organization_no 찾기
    const targetOrgNos = new Set<number>();
    for (const [orgNo, employees] of Object.entries(employeesMap)) {
      if (employees.some(e => e.user_no === myUserNo)) {
        targetOrgNos.add(Number(orgNo));
      }
    }
    if (targetOrgNos.size === 0) return new Set<number>();
    // 해당 조직까지의 경로(부모 조직들) 수집
    const pathNos = new Set<number>();
    findAllPathNos(treeData, targetOrgNos, pathNos);
    return pathNos;
  }, [myUserNo, employeesMap, treeData]);

  // 검색 쿼리 처리 (컴포넌트 수준 디바운싱 제거 - 부모에서 엔터/클릭 시점에만 query가 변경됨)
  useEffect(() => {
    const searchText = query.text;
    if (!searchText || !cno) {
      setSearchResults([]);
      setExpandedNodes(new Set());
      setHighlightedUserNos(new Set());
      setHighlightedOrgNos(new Set());
      setIsSearching(false);
      setScrollTargetIndex(0);
      return;
    }

    // 새로운 검색어라면 인덱스 초기화, 이전과 같다면 인덱스만 증가 (Find Next)
    if (query.version === 0) {
      setScrollTargetIndex(0);
    } else {
      setScrollTargetIndex(prev => prev + 1);
      // 이미 결과가 있는 상태에서 엔터를 또 쳤으므로 API 재호출 없이 종료 가능 (단, 초기 로딩 방지 목적)
      if (searchResults.length > 0) return;
    }

    const performSearch = async () => {
      setIsSearching(true);
      try {
        const res = await inviteAPI.searchOrganizationEmployees(cno, searchText);
        if (res?.resultData && Array.isArray(res.resultData)) {
          const results: SearchResultItem[] = res.resultData;
          setSearchResults(results);

          // 하이라이트 대상 및 부서 확장 대상 분리
          const targetUserNos = new Set<number>();
          const targetOrgNosForHighlight = new Set<number>();
          const allOrgNosToExpand = new Set<number>();

          results.forEach(r => {
            const uNo = Number(r.user_no);
            const oNo = Number(r.organization_no);
            
            // 전체 경로 확장을 위해 무조건 추가
            if (oNo) allOrgNosToExpand.add(oNo);

            // 하이라이트 결정: 사용자명이 검색어를 포함하면 사용자만 하이라이트, 
            // 그렇지 않고 부서명이 포함되면 부서 하이라이트
            const isUserMatch = r.user_name?.toLowerCase().includes(searchText.toLowerCase());
            const isOrgMatch = r.organization_name?.toLowerCase().includes(searchText.toLowerCase());

            if (isUserMatch) {
              targetUserNos.add(uNo);
            } else if (isOrgMatch) {
              targetOrgNosForHighlight.add(oNo);
            }
          });

          // 경로 탐색: 모든 타겟 부서까지의 부모를 포함
          const pathNos = new Set<number>();
          findAllPathNos(treeData, allOrgNosToExpand, pathNos);
          setExpandedNodes(pathNos);

          setHighlightedUserNos(targetUserNos);
          setHighlightedOrgNos(targetOrgNosForHighlight);
        } else {
          setSearchResults([]);
          setExpandedNodes(new Set());
          setHighlightedUserNos(new Set());
          setHighlightedOrgNos(new Set());
        }
      } catch (error) {
        console.error('Failed to search organization employees', error);
        setSearchResults([]);
        setExpandedNodes(new Set());
        setHighlightedUserNos(new Set());
        setHighlightedOrgNos(new Set());
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [query, cno, treeData]);

  // 특정 인덱스의 하이라이트 항목으로 자동 스크롤
  useEffect(() => {
    if ((highlightedUserNos.size > 0 || highlightedOrgNos.size > 0) && containerRef.current) {
      // 트리 확장(isOpen 업데이트) 및 DOM 렌더링 시간을 충분히 주기 위해 지연 시간 증가
      const timer = setTimeout(() => {
        const highlights = containerRef.current?.querySelectorAll('.highlight');
        if (highlights && highlights.length > 0) {
          // 인덱스 순환 처리
          const target = highlights[scrollTargetIndex % highlights.length];
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [highlightedUserNos, highlightedOrgNos, scrollTargetIndex]);

  if (isLoading) {
    return <div className="rtcOrganization__top chatbox--empty scrollCustom">
        <div className="empty_area">
          <span className="ico"><img src={"https://static.wehago.com/imgs/common/ico_nodata140.png"} width="42" alt=""/></span>
          <p className="empty_txt">불러오는 중입니다...</p>
        </div>
      </div>
    
    
    ;
  }

  // 검색 결과가 없을 때 (엠티 케이스)
  if (query.text && !isSearching && searchResults.length === 0) {
    return (
      <div className="rtcNew_joinMemberListWrap chatbox--empty scrollCustom">
        <div className="empty_area">
          <span className="ico"><img src="https://static.wehago.com/imgs/common/ico_nodata138.png" width="42" alt="" /></span>
          <p className="empty_txt">찾으시는 참석자가 없어요.<br />이름을 다시 확인해주세요.</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="rtcOrganization__top scrollCustom">
       {treeData.map(node => (
          <TreeNode
            key={`root_${node.organization_no}`}
            node={node}
            employeesMap={employeesMap}
            selectedIds={selectedIds}
            onToggleUser={onToggleUser}
            onRemoveUserFromOrg={onRemoveUserFromOrg}
            cno={cno}
            companyNo={companyNo}
            expandedNodes={expandedNodes.size > 0 ? expandedNodes : myOrgExpandedNodes}
            highlightedUserNos={highlightedUserNos}
            highlightedOrgNos={highlightedOrgNos}
            allSearchResults={searchResults}
            connectedPortalIds={connectedPortalIds}
          />
       ))}
    </div>
  );
};

export default OrganizationTab;
